import { GRAPH_VERSION, isObjectEndpoint } from './graphModel.js';
import { serializeStorageValue } from '../utils/storageSerialization.js';

/**
 * Design-file form of a page graph.
 *
 * In memory a wire references a UI object by Designer uuid; uuids are not
 * persisted for ordinary objects, so the file references the object by its
 * page-local OpenHASP id instead: `{ objectId, port }`, at either wire end.
 */
export function serializeGraph(graph, haspidOfObject) {
    const toFile = endpoint => {
        const objectId = haspidOfObject(endpoint.object);
        return objectId === null || objectId === undefined ? null : { objectId: Number(objectId), port: endpoint.port };
    };
    const wires = [];
    for (const wire of graph.wires) {
        const copy = serializeStorageValue(wire, '$.wires[]');
        if (isObjectEndpoint(wire.from)) copy.from = toFile(wire.from);
        if (isObjectEndpoint(wire.to)) copy.to = toFile(wire.to);
        // A wire to an object that no longer exists has nothing to reconnect to.
        if (!copy.from || !copy.to) continue;
        wires.push(copy);
    }
    return {
        version: GRAPH_VERSION,
        nodes: serializeStorageValue(graph.nodes, '$.nodes'),
        wires,
    };
}

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * @param {object} data            file form
 * @param {(haspid: number) => string|undefined} uuidOfHaspid
 * @param {object} options
 * @param {(type: string) => object|null} options.getNodeSchema  the domain's node schemas
 * @param {string} [options.label]          prefix of diagnostics ("Logic", …)
 * @param {boolean} [options.objectTargets] whether a wire may end on a UI object
 * @returns {{ graph, diagnostics: string[] }} — invalid entries are dropped and reported.
 */
export function deserializeGraph(data, uuidOfHaspid, { getNodeSchema, label = 'Graph', objectTargets = false }) {
    const diagnostics = [];
    const nodes = [];
    for (const node of Array.isArray(data?.nodes) ? data.nodes : []) {
        const schema = getNodeSchema(node?.type);
        if (!schema || typeof node.id !== 'string' || !isPlainObject(node.data)) {
            diagnostics.push(`${label} node ${node?.id ?? '?'} has an unknown type or invalid data and was dropped`);
            continue;
        }
        nodes.push({
            id: node.id,
            kind: schema.kind,
            type: node.type,
            x: Number(node.x) || 0,
            y: Number(node.y) || 0,
            data: structuredClone(node.data),
        });
    }
    const nodeIds = new Set(nodes.map(node => node.id));
    // { endpoint } | { missing: objectId } | null (no valid reference)
    const resolve = endpoint => {
        if (endpoint?.objectId !== undefined) {
            const uuid = uuidOfHaspid(Number(endpoint.objectId));
            return uuid ? { endpoint: { object: uuid, port: endpoint.port } } : { missing: endpoint.objectId };
        }
        return nodeIds.has(endpoint?.node) ? { endpoint: { node: endpoint.node, port: endpoint.port } } : null;
    };
    const wires = [];
    for (const wire of Array.isArray(data?.wires) ? data.wires : []) {
        const to = typeof wire?.id === 'string' && (objectTargets || wire.to?.objectId === undefined)
            ? resolve(wire.to)
            : null;
        if (!to?.endpoint && !to?.missing) {
            diagnostics.push(`${label} wire ${wire?.id ?? '?'} has no valid target and was dropped`);
            continue;
        }
        const from = resolve(wire.from);
        const missing = to.missing ?? from?.missing;
        if (missing !== undefined) {
            diagnostics.push(`${label} wire ${wire.id} references missing object id ${missing} and was dropped`);
            continue;
        }
        if (!from) {
            diagnostics.push(`${label} wire ${wire.id} has no valid source and was dropped`);
            continue;
        }
        wires.push({
            id: wire.id,
            from: from.endpoint,
            to: to.endpoint,
            ...(wire.order !== undefined ? { order: Number(wire.order) } : {}),
        });
    }
    return { graph: { version: GRAPH_VERSION, nodes, wires }, diagnostics };
}
